import { http } from '@/api/http';
import { ApiResponseBody } from '@/api/types';
import { getOpobffCaseApiUrl } from './opobff-case.paths';
import {
  AssignOpoCaseContactsRequestBody,
  ChangeCaseAssigneesRequestBody,
  CreateOpoDraftCaseRequestBody,
  CreateOpoDraftCaseResponse,
  GetOpoCaseActivityLogsResponse,
  GetOpoCasesQueryParams,
  GetOpoCasesResponse,
  OpoNrpApplicabilityAndTypeRecord,
  UnassignOpoCaseContactsRequestBody,
  UpdateOpoCaseDonorDataRequestBody,
  UpdateOpoCaseRequestBody,
  UpdateOpoNrpApplicabilityAndTypeRequestBody,
} from './opobff-case.types';

const unwrapOpoResponse = <T>(body: ApiResponseBody<T>, fallbackMessage: string): T => {
  if (body.hasError) {
    throw new Error(body.error?.message || fallbackMessage);
  }
  return body.data;
};

const createOpoDraftCase = async (body: CreateOpoDraftCaseRequestBody): Promise<CreateOpoDraftCaseResponse> => {
  const res = await http.post<ApiResponseBody<CreateOpoDraftCaseResponse>>(getOpobffCaseApiUrl.createOpoDraft(), body);
  return unwrapOpoResponse(res.data, 'Failed to create OPO case');
};

const getOpoCases = async (params: GetOpoCasesQueryParams): Promise<GetOpoCasesResponse> => {
  const res = await http.get<ApiResponseBody<GetOpoCasesResponse>>(getOpobffCaseApiUrl.opoCases(), {
    params,
    paramsSerializer: { indexes: null },
  });
  return res.data.data;
};

const getOpoCaseById = async (caseId: string): Promise<CreateOpoDraftCaseResponse> => {
  const res = await http.get<ApiResponseBody<CreateOpoDraftCaseResponse>>(getOpobffCaseApiUrl.opoCaseById(caseId));
  if (res.data.hasError) {
    throw new Error(res.data.error?.message ?? 'Failed to load OPO case');
  }
  return res.data.data;
};

const updateOpoCaseDonorData = async ({
  caseId,
  ...body
}: UpdateOpoCaseDonorDataRequestBody & { caseId: string }): Promise<void> => {
  const res = await http.patch<ApiResponseBody<void>>(getOpobffCaseApiUrl.opoCaseDonorData(caseId), body);
  return unwrapOpoResponse(res.data, 'Failed to update donor data');
};

const updateOpoCase = async ({ caseId, ...body }: UpdateOpoCaseRequestBody & { caseId: string }): Promise<void> => {
  const res = await http.patch<ApiResponseBody<void>>(getOpobffCaseApiUrl.opoCaseById(caseId), body);
  return unwrapOpoResponse(res.data, 'Failed to update OPO case');
};

const deleteOpoDraftCase = async (caseId: string): Promise<void> => {
  const res = await http.delete<ApiResponseBody<void>>(getOpobffCaseApiUrl.deleteOpoDraftCase(caseId));
  return res.data.data;
};

const getOpoCaseActivityLogs = async (caseId: string): Promise<GetOpoCaseActivityLogsResponse> => {
  const res = await http.get<ApiResponseBody<GetOpoCaseActivityLogsResponse>>(
    getOpobffCaseApiUrl.opoCaseActivityLogs(caseId),
  );
  return res.data.data;
};

const updateOpoCaseNrpApplicabilityAndType = async ({
  caseId,
  ...body
}: UpdateOpoNrpApplicabilityAndTypeRequestBody & { caseId: string }): Promise<OpoNrpApplicabilityAndTypeRecord> => {
  const res = await http.post<ApiResponseBody<OpoNrpApplicabilityAndTypeRecord>>(
    getOpobffCaseApiUrl.opoCaseNrpApplicabilityAndType(caseId),
    body,
  );
  return res.data.data;
};

const assignOpoCaseContacts = async ({
  caseId,
  ...body
}: AssignOpoCaseContactsRequestBody & { caseId: string }): Promise<CreateOpoDraftCaseResponse> => {
  const res = await http.post<ApiResponseBody<CreateOpoDraftCaseResponse>>(
    getOpobffCaseApiUrl.opoCaseContactsAssignees(caseId),
    body,
  );
  return res.data.data;
};

const unassignOpoCaseContacts = async ({
  caseId,
  ...body
}: UnassignOpoCaseContactsRequestBody & { caseId: string }): Promise<CreateOpoDraftCaseResponse> => {
  const res = await http.delete<ApiResponseBody<CreateOpoDraftCaseResponse>>(
    getOpobffCaseApiUrl.opoCaseContactsAssignees(caseId),
    { data: body },
  );
  return res.data.data;
};

const changeCaseAssignees = async ({
  caseId,
  assignees,
}: {
  caseId: string;
  assignees: ChangeCaseAssigneesRequestBody;
}): Promise<CreateOpoDraftCaseResponse> => {
  const res = await http.patch<ApiResponseBody<CreateOpoDraftCaseResponse>>(
    getOpobffCaseApiUrl.caseAssignees(caseId),
    assignees,
  );
  return res.data.data;
};

export const opobffCaseApiFetcher = {
  getOpoCases,
  getOpoCaseById,
  createOpoDraftCase,
  updateOpoCase,
  updateOpoCaseDonorData,
  deleteOpoDraftCase,
  getOpoCaseActivityLogs,
  updateOpoCaseNrpApplicabilityAndType,
  assignOpoCaseContacts,
  unassignOpoCaseContacts,
  changeCaseAssignees,
};
